import { type JSX, useEffect, useState } from 'react';

import { kostentraeger as kostentraegerListe, werte, type Gutscheinwert, type Kostentraeger } from '../api/bgzuschuss';
import { ApiFehler } from '../api/fehler';
import { vorschau as gutscheinvorschau, type Gutscheinvorschau } from '../api/gutscheine';
import { alsEuro } from '../betrag';
import { Dialog } from '../komponenten/Dialog';

const BETRAG = /^\d{1,10}(\.\d{1,2})?$/;

/** PRD-38: Gutschein eines Kostenträgers, an eine Zeile der Maske gebunden. */
export type BgAnrechnung = {
  readonly art: 'bg';
  readonly zeileNr: number;
  readonly kostentraeger: Kostentraeger;
  readonly nummer: string;
  readonly name: string;
  readonly gutscheinwert: Gutscheinwert | null;
  readonly betrag: string;
};

/** GUT-14: ein geprüfter Wertgutschein — eingelöst wird erst beim Anlegen. */
export type WertAnrechnung = {
  readonly art: 'wert';
  readonly gutschein: string;
  readonly code: string;
  readonly nummer: string;
  readonly restwert: string;
  /** Was die Prüfung gegen den vorläufigen offenen Betrag ergab. */
  readonly anrechnung: string;
};

/**
 * „Gutschein anrechnen" in der Maske „Neue Rechnung" (Auftrag CRM-174).
 *
 * ZWEI ARTEN, EIN KNOPF. Der Gutschein eines Kostenträgers (PRD-38, E-74)
 * hängt an einer Zeile und mindert den Zahlbetrag; der Wertgutschein mit Code
 * (GUT-14, PRF-17) ist eine Zahlung und wird höchstens bis zum offenen Betrag
 * angerechnet. Beides prüft dieses Fenster nur — angelegt, eingelöst und
 * gerechnet wird auf dem Server in der Transaktion, die auch den Entwurf
 * erzeugt. Verkauft wird hier kein Gutschein.
 */
export function GutscheinAnrechnen({
  zeilen,
  offenerBetrag,
  leistungstag,
  schonGeprueft,
  beiAnrechnen,
  beiSchliessen,
}: {
  readonly zeilen: readonly { readonly nr: number; readonly text: string }[];
  readonly offenerBetrag: string;
  readonly leistungstag: string | null;
  readonly schonGeprueft: readonly string[];
  readonly beiAnrechnen: (anrechnung: BgAnrechnung | WertAnrechnung) => void;
  readonly beiSchliessen: () => void;
}): JSX.Element {
  const [art, setzeArt] = useState<'bg' | 'wert'>('bg');
  const [traeger, setzeTraeger] = useState<readonly Kostentraeger[] | undefined>(undefined);
  const [traegerId, setzeTraegerId] = useState('');
  const [liste, setzeListe] = useState<readonly Gutscheinwert[] | undefined>(undefined);
  const [wertId, setzeWertId] = useState('');
  const [eigenerBetrag, setzeEigenerBetrag] = useState('');
  const [zeileNr, setzeZeileNr] = useState<number | undefined>(zeilen[0]?.nr);
  const [nummer, setzeNummer] = useState('');
  const [name, setzeName] = useState('');
  const [code, setzeCode] = useState('');
  const [pruefung, setzePruefung] = useState<Gutscheinvorschau | undefined>(undefined);
  const [prueft, setzePrueft] = useState(false);
  const [fehler, setzeFehler] = useState<string | undefined>(undefined);

  useEffect(() => {
    void kostentraegerListe()
      .then(setzeTraeger)
      .catch(() => setzeTraeger([]));
  }, []);

  useEffect(() => {
    setzeListe(undefined);
    setzeWertId('');
    if (traegerId === '') return;
    void werte(traegerId)
      .then((w) => setzeListe(w.filter((x) => x.aktiv)))
      .catch(() => setzeListe([]));
  }, [traegerId]);

  const gewaehlterWert = liste?.find((w) => w.id === wertId);
  const betrag = wertId === '' ? eigenerBetrag.trim().replace(',', '.') : (gewaehlterWert?.betrag ?? '');
  const bgBereit =
    traegerId !== '' &&
    zeileNr !== undefined &&
    BETRAG.test(betrag) &&
    Number(betrag) > 0 &&
    nummer.trim() !== '' &&
    name.trim() !== '';

  async function pruefen(): Promise<void> {
    setzePrueft(true);
    setzeFehler(undefined);
    setzePruefung(undefined);
    try {
      setzePruefung(await gutscheinvorschau(code.trim(), offenerBetrag, leistungstag));
    } catch (ursache) {
      setzeFehler(ursache instanceof ApiFehler ? ursache.meldung.wasGeschah : 'Der Code ließ sich nicht prüfen.');
    } finally {
      setzePrueft(false);
    }
  }

  const doppelt = pruefung?.gefunden === true && schonGeprueft.includes(pruefung.gutschein.id);
  const wertBereit =
    pruefung?.gefunden === true && pruefung.einloesbar && Number(pruefung.anrechnung) > 0 && !doppelt;

  function anrechnen(): void {
    if (art === 'bg') {
      const kt = traeger?.find((t) => t.id === traegerId);
      if (!bgBereit || kt === undefined || zeileNr === undefined) return;
      beiAnrechnen({
        art: 'bg',
        zeileNr,
        kostentraeger: kt,
        nummer: nummer.trim(),
        name: name.trim(),
        gutscheinwert: gewaehlterWert ?? null,
        betrag,
      });
      return;
    }
    if (!wertBereit || pruefung?.gefunden !== true) return;
    beiAnrechnen({
      art: 'wert',
      gutschein: pruefung.gutschein.id,
      code: code.trim(),
      nummer: pruefung.gutschein.nummer,
      restwert: pruefung.gutschein.restwert,
      anrechnung: pruefung.anrechnung,
    });
  }

  return (
    <Dialog titel="Gutschein anrechnen" unterzeile="Mindert den Zahlbetrag, nicht den Rechnungsbetrag" beiSchliessen={beiSchliessen}>
      <fieldset className="feld">
        <legend className="feld__beschriftung">Art</legend>
        <label>
          <input type="radio" name="gutscheinart" checked={art === 'bg'} onChange={() => setzeArt('bg')} /> Gutschein
          eines Kostenträgers
        </label>
        <label>
          <input type="radio" name="gutscheinart" checked={art === 'wert'} onChange={() => setzeArt('wert')} />{' '}
          Wertgutschein mit Code
        </label>
      </fieldset>

      {art === 'bg' ? (
        <>
          {/* PRD-38, E-74 */}
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="anrechnung-traeger">
              Kostenträger
            </label>
            <select id="anrechnung-traeger" value={traegerId} onChange={(e) => setzeTraegerId(e.target.value)}>
              <option value="">{traeger === undefined ? 'Wird geladen …' : 'Bitte wählen'}</option>
              {(traeger ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.kundennummer})
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="anrechnung-wert">
              Gutscheinwert
            </label>
            <select id="anrechnung-wert" value={wertId} disabled={traegerId === ''} onChange={(e) => setzeWertId(e.target.value)}>
              <option value="">Eigener Betrag</option>
              {(liste ?? []).map((w) => (
                <option key={w.id} value={w.id}>
                  {w.bezeichnung} ({alsEuro(w.betrag)})
                </option>
              ))}
            </select>
          </div>
          {wertId === '' && (
            <div className="feld">
              <label className="feld__beschriftung" htmlFor="anrechnung-betrag">
                Betrag
              </label>
              <input
                id="anrechnung-betrag"
                type="text"
                inputMode="decimal"
                value={eigenerBetrag}
                onChange={(e) => setzeEigenerBetrag(e.target.value)}
              />
            </div>
          )}
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="anrechnung-zeile">
              Für Position
            </label>
            <select
              id="anrechnung-zeile"
              value={zeileNr ?? ''}
              onChange={(e) => setzeZeileNr(e.target.value === '' ? undefined : Number(e.target.value))}
            >
              {zeilen.length === 0 && <option value="">Noch keine Position</option>}
              {zeilen.map((z) => (
                <option key={z.nr} value={z.nr}>
                  {z.text}
                </option>
              ))}
            </select>
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="anrechnung-nummer">
              Gutscheinnummer
            </label>
            <input id="anrechnung-nummer" type="text" maxLength={60} value={nummer} onChange={(e) => setzeNummer(e.target.value)} />
          </div>
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="anrechnung-name">
              Teilnehmer
            </label>
            <input id="anrechnung-name" type="text" maxLength={200} value={name} onChange={(e) => setzeName(e.target.value)} />
          </div>
        </>
      ) : (
        <>
          {/* GUT-14, GUT-20 */}
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="anrechnung-code">
              Gutscheincode
            </label>
            <input
              id="anrechnung-code"
              type="text"
              maxLength={40}
              value={code}
              onChange={(e) => {
                setzeCode(e.target.value);
                setzePruefung(undefined);
              }}
            />
          </div>
          <div className="knopfreihe">
            <button type="button" className="knopf" disabled={code.trim() === '' || prueft} onClick={() => void pruefen()}>
              {prueft ? 'Wird geprüft …' : 'Prüfen'}
            </button>
          </div>
          {fehler !== undefined && <p className="warnung" role="status">{fehler}</p>}
          {pruefung?.gefunden === false && (
            <p className="warnung" role="status">
              Diesen Code gibt es nicht.
            </p>
          )}
          {pruefung?.gefunden === true && (
            <p className={pruefung.einloesbar && !doppelt ? 'hinweiszeile' : 'warnung'} role="status">
              Gutschein {pruefung.gutschein.nummer}: Restwert {alsEuro(pruefung.gutschein.restwert)}.{' '}
              {doppelt
                ? 'Er ist schon angerechnet.'
                : pruefung.einloesbar
                  ? `Angerechnet würden ${alsEuro(pruefung.anrechnung)} — eingelöst wird beim Anlegen, höchstens der offene Betrag.`
                  : (pruefung.meldung ?? 'Er ist nicht einlösbar.')}
            </p>
          )}
        </>
      )}

      <div className="knopfreihe">
        <button type="button" className="knopf" onClick={beiSchliessen}>
          Abbrechen
        </button>
        <button type="button" className="knopf knopf--haupt" disabled={art === 'bg' ? !bgBereit : !wertBereit} onClick={anrechnen}>
          Anrechnen
        </button>
      </div>
    </Dialog>
  );
}
